/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useState } from 'react'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import moment from 'moment'

import {
  DealsProposalCurrencyChoices,
  ProposalNode,
  useProposalQuery,
} from '@/apollo/graphql'
import { homePath, transformData } from '@/utils'
import { IProposal, TFormSubmit } from '@/types'
import { useAPI, useToast } from '@/hooks'
import { t } from '@/i18n'

interface IUseData {
  id?: string
}

interface IUseDataRes {
  loading: boolean
  submitting: boolean
  data: Partial<ProposalNode> | undefined
  onSubmit: TFormSubmit<ProposalNode>
  onCancel: () => void
  onDelete: (cb: () => void) => void
}

const defaultData: Partial<IProposal> = {
  currency: DealsProposalCurrencyChoices.Ils,
}

export const useData = ({ id }: IUseData): IUseDataRes => {
  const addToast = useToast()
  const navigate: NavigateFunction = useNavigate()
  const { submitting, sendForm } = useAPI()
  const [data, setData] = useState<Partial<IProposal>>(defaultData)

  const { loading, refetch } = useProposalQuery({
    skip: !id,
    fetchPolicy: 'network-only',
    variables: {
      id: id || '',
    },
    onCompleted: res => {
      if (res.proposal) {
        // @ts-ignore
        setData({
          ...(res.proposal || {}),
          currency: res.proposal.currency || DealsProposalCurrencyChoices.Ils,
        })
      }
    },
  })

  useEffect(() => {
    if (!id) {
      setData(defaultData)
    }
  }, [id])

  const onSubmit: TFormSubmit<IProposal> = useCallback(
    (path, isEdit, callback) => d => {
      if (d.validTo) {
        d.validTo = moment(d.validTo).format('YYYY-MM-DD')
      }

      sendForm(
        {
          path,
          method: isEdit ? 'PUT' : 'POST',
          data: transformData(d),
          callback: e => {
            callback?.(e)
            refetch()
          },
        },
        { showNotify: true },
      )
    },
    [refetch, sendForm],
  )

  const onCancel = useCallback(() => {
    navigate(homePath)
  }, [navigate])

  const onDelete = useCallback(
    (cb: () => void) => {
      sendForm(
        {
          path: `deals/proposal/${id}/`,
          method: 'DELETE',
          data: undefined,
          callback: () => {
            cb?.()
            addToast('success', t('success'))
          },
        },
        {
          showNotify: false,
        },
      )
    },
    [addToast, sendForm, id],
  )

  return {
    loading,
    submitting,
    data,
    onSubmit,
    onCancel,
    onDelete,
  }
}
